import React, { useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { AuthProvider } from "./AuthContext";
import { StudentAuthProvider, ProtectedStudentRoute } from "./StudentAuthContext";
import { SubAgnetAuthProvider, ProtectedSubAgentRoute } from "./pages/Clients/SubAgent/SubAgentAuthContext";
import { FilterProvider } from "./context/FilterContext";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import { ProtectedRoute } from "./AuthContext";

// Common
import Login from "./pages/Login";
import { default as Dashboard } from "./pages/Clients/Common/Dashboard";
import LeadManagement from "./pages/Clients/Common/LeadManagement";



import Tickets from "./pages/Clients/Common/Tickets";
import StudentDetails from "./pages/Clients/Common/StudentDetails";
// import StudentDetails from "./pages/Clients/Common/StudentDetailsScreen_2";
import FollowUps from "./pages/Clients/Common/FollowUps";
import AllStudents from "./pages/Clients/Common/AllStudents";
import Reports from "./pages/Clients/Common/Reports";
import Invoices from "./pages/Clients/Common/Invoices";

// Admin
import CallCrm from "./pages/Clients/Admin/CallCrm";
import Escalations from "./pages/Clients/Admin/Escalations";
import StaffManagement from "./pages/Clients/Admin/Staffmanagement";
import AccountNewCustomer from "./pages/Clients/Admin/Accounts/AccountNewCustomer";
import AccountNewInvoice from "./pages/Clients/Admin/Accounts/AccountNewInvoice";
import PayoutsAccounts from "./pages/Clients/Admin/Accounts/PayoutsAccounts";
import InvoiceListAccount from "./pages/Clients/Admin/Accounts/InvoiceListAccount";
import ServicesAdmin from "./pages/Clients/Admin/ServicesAdmin";
import Settings from "./pages/Clients/Admin/Settings";
import AuditLogs from "./pages/Clients/Admin/AuditLogs";
import MigrationBeta from "./pages/Clients/Admin/MigrationBeta";

// BDAs
import StaffTasks from "./pages/Clients/Common/Tasks";
import Collaborations from "./pages/Clients/Staff/Collaborations";
import SubLeadManagement from "./pages/Clients/SubAgent/LeadManagement";

// SubAgent
import SubAgentLogin from "./pages/Clients/SubAgent/SubAgentLogin";
import SubAgentDashboard from "./pages/Clients/SubAgent/SubagentDashboard";
// import SubTools from "./pages/Clients/SubAgent/Tools";

// Student
import StudentDashboard from "./pages/Student/StudentDashboard";
import StudentRaiseTicket from "./pages/Student/StudentRaiseTicket";
import StudentEscalations from "./pages/Student/StudentEscalations";
import StudentDocUpload from "./pages/Student/StudentDocUpload";
import StudentMyProfile from "./pages/Student/StudentMyProfile";
import StudentsServices from "./pages/Student/StudentsServices";

// Components
import SideNavbar from "./components/SideNavbar";
import NotFound from "./pages/Error/NotFound";
import LeadByStage from "./components/LeadByStage";
import StudentLogin from "./pages/StudentLogin";
import TopNavbar from "./components/TopNavbar";
import Migration from "./pages/Clients/Admin/Migration";
import Tools from "./pages/Clients/Common/Tools";
import Integrations from "./pages/Clients/Admin/Integrations";
import MetaAds from "./components/integrations/MetaAds";
import WhatsappBusinessCloud from "./components/integrations/WhatsappBusinessCloud";
import MetaAdsForFaceBookReview from "./components/integrations/MetaAdsForFaceBookReview";
import WhatsappBusinessCloudClient from "./pages/Clients/Common/WhatsappBusinessCloudClient";
import GoogleAds from "./components/integrations/GoogleAds";
import Webhooks from "./components/integrations/Webhooks";
import CloudTelephony from "./components/integrations/cloudTelephony/Index";
import Exotel from "./components/integrations/cloudTelephony/Exotel";
import Gmail from "./components/integrations/Gmail";
import GmailTemplates from "./components/integrations/GmailTemplates";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";

const App: React.FC = () => {

  // Layout component with header
  const Layout = ({ children }: { children: React.ReactNode }) => {
    return (
      <div className="flex flex-col min-h-screen bg-gray-100">
        <header className="w-full fixed top-0 z-10">
          <TopNavbar />
        </header>
        <main className="flex-grow">
          {children}
        </main>
      </div>
    );
  };

  // Layout wrapper component for protected routes
  const ProtectedLayout = ({ children, requireAdmin = false, requireManager = false, requireStaff = false }: { children: React.ReactNode, requireAdmin?: boolean, requireManager?: boolean, requireStaff?: boolean }) => {
    return (
      <ProtectedRoute requireAdmin={requireAdmin} requireManager={requireManager} requireStaff={requireStaff}>
        <Layout>
          {children}
        </Layout>
      </ProtectedRoute>
    );
  };

  // Layout wrapper component for protected student routes
  const ProtectedStudentLayout = ({ children }: { children: React.ReactNode }) => {
    return (
      <ProtectedStudentRoute>
        <Layout>
          {children}
        </Layout>
      </ProtectedStudentRoute>
    );
  };

  // Layout wrapper component for protected subagent routes
  const ProtectedSubAgentLayout = ({ children }: { children: React.ReactNode }) => {
    return (
      <ProtectedSubAgentRoute>
        <Layout>
          {children}
        </Layout>
      </ProtectedSubAgentRoute>
    );
  }

  return (
    <Provider store={store}>
      <Router>
        <AuthProvider>
          <SubAgnetAuthProvider>
          <StudentAuthProvider>
            <FilterProvider>
              <Routes>
                {/* Login routes without header */}
                <Route path="/" element={<Login />} />
                <Route path="/student-Login" element={<StudentLogin />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/integrations/meta-ads-review" element={<MetaAdsForFaceBookReview />} />

                {/* Admin Routes with header */}
                <Route path="/sidenav" element={<ProtectedLayout requireAdmin><SideNavbar /></ProtectedLayout>} />
                <Route path="/teammanagement" element={<ProtectedLayout requireAdmin><StaffManagement /></ProtectedLayout>} />
                <Route path="/crm/staff" element={<ProtectedLayout requireAdmin><StaffManagement /></ProtectedLayout>} />
                <Route path="/leadstage" element={<ProtectedLayout requireAdmin><LeadByStage /></ProtectedLayout>} />
                <Route path="/admin/dashboard" element={<ProtectedLayout requireAdmin><Dashboard /></ProtectedLayout>} />
                <Route path="/escalation" element={<ProtectedLayout requireAdmin><Escalations /></ProtectedLayout>} />
                <Route path="/accounts" element={<ProtectedLayout requireAdmin><AccountNewCustomer /></ProtectedLayout>} />
                <Route path="/service" element={<ProtectedLayout><ServicesAdmin /></ProtectedLayout>} />
                <Route path="/accounts/invoice" element={<ProtectedLayout requireAdmin><AccountNewInvoice /></ProtectedLayout>} />
                <Route path="/accounts/payouts" element={<ProtectedLayout requireAdmin><PayoutsAccounts /></ProtectedLayout>} />
                <Route path="/accounts/invoicelist" element={<ProtectedLayout requireAdmin><InvoiceListAccount /></ProtectedLayout>} />
                <Route path="/settings" element={<ProtectedLayout requireAdmin><Settings /></ProtectedLayout>} />
                <Route path="/audit-logs" element={<ProtectedLayout requireAdmin><AuditLogs /></ProtectedLayout>} />
                <Route path="/integrations" element={<ProtectedLayout requireAdmin><Integrations /></ProtectedLayout>} />
                <Route path="/integrations/meta-ads" element={<ProtectedLayout requireAdmin><MetaAds /></ProtectedLayout>} />
                <Route path="/integrations/whatsapp-business-cloud" element={<ProtectedLayout requireAdmin><WhatsappBusinessCloud /></ProtectedLayout>} />
                <Route path="/integrations/google-ads" element={<ProtectedLayout requireAdmin><GoogleAds /></ProtectedLayout>} />
                <Route path="/integrations/webhooks" element={<ProtectedLayout requireAdmin><Webhooks /></ProtectedLayout>} />
                <Route path="/integrations/cloud-telephony" element={<ProtectedLayout requireAdmin><CloudTelephony /></ProtectedLayout>} />
                <Route path="/integrations/cloud-telephony/exotel" element={<ProtectedLayout requireAdmin><Exotel /></ProtectedLayout>} />
                <Route path="/migration-beta" element={<ProtectedLayout requireAdmin><MigrationBeta /></ProtectedLayout>} />

                {/* Manager Routes with header */}
                <Route path="/manager/dashboard" element={<ProtectedLayout requireManager><Dashboard /></ProtectedLayout>} />
                <Route path="/manager/escalation" element={<ProtectedLayout requireManager><Escalations /></ProtectedLayout>} />

                {/* BDA Routes with header */}
                <Route path="/staff/dashboard" element={<ProtectedLayout requireStaff><Dashboard /></ProtectedLayout>} />

                {/* SubAgent Routes without header */}
                <Route path="/subagent/dashboard" element={<ProtectedSubAgentLayout><SubAgentDashboard /></ProtectedSubAgentLayout>} />
                <Route path="/subagent/leadmanagement" element={<ProtectedSubAgentLayout><SubLeadManagement /></ProtectedSubAgentLayout>} />
                <Route path="/subagent/tools" element={<ProtectedSubAgentLayout><Tools /></ProtectedSubAgentLayout>} />
                <Route path="/subagent/studentdetails/:leadId" element={<ProtectedSubAgentLayout><StudentDetails /></ProtectedSubAgentLayout>} />

                {/* Protected Routes with header  */}
                <Route path="/leadmanagement" element={<ProtectedLayout><LeadManagement /></ProtectedLayout>} />
                <Route path="/migration" element={<ProtectedLayout><Migration /></ProtectedLayout>} />




                <Route path="/callcrm" element={<ProtectedLayout><CallCrm /></ProtectedLayout>} />
                <Route path="/ticket" element={<ProtectedLayout><Tickets /></ProtectedLayout>} />
                <Route path="/students" element={<ProtectedLayout><AllStudents /></ProtectedLayout>} />
                <Route path="/collaborations" element={<ProtectedLayout><Collaborations /></ProtectedLayout>} />
                <Route path="/followups" element={<ProtectedLayout><FollowUps /></ProtectedLayout>} />
                <Route path="/staffstask" element={<ProtectedLayout><StaffTasks /></ProtectedLayout>} />
                <Route path="/studentdetails/:leadId" element={<ProtectedLayout><StudentDetails /></ProtectedLayout>} />
                <Route path="/reports" element={<ProtectedLayout><Reports /></ProtectedLayout>} />
                <Route path="/tools" element={<ProtectedLayout><Tools /></ProtectedLayout>} />
                <Route path="/whatsapp-business-cloud" element={<ProtectedLayout><WhatsappBusinessCloudClient /></ProtectedLayout>} />
                <Route path="/invoices" element={<ProtectedLayout><Invoices /></ProtectedLayout>} />
                <Route path="/integrations/gmail" element={<ProtectedLayout><Gmail /></ProtectedLayout>} />
                <Route path="/integrations/gmail-templates" element={<ProtectedLayout><GmailTemplates /></ProtectedLayout>} />

                {/* Student Routes with header */}
                <Route path="/student-dashboard" element={<ProtectedStudentLayout><StudentDashboard /></ProtectedStudentLayout>} />
                <Route path="/student-raise-tickets" element={<ProtectedStudentLayout><StudentRaiseTicket /></ProtectedStudentLayout>} />
                <Route path="/student-escalations" element={<ProtectedStudentLayout><StudentEscalations /></ProtectedStudentLayout>} />
                <Route path="/student-documents" element={<ProtectedStudentLayout><StudentDocUpload /></ProtectedStudentLayout>} />
                <Route path="/student-services" element={<ProtectedStudentLayout><StudentsServices /></ProtectedStudentLayout>} />
                <Route path="/student-profile" element={<ProtectedStudentLayout><StudentMyProfile /></ProtectedStudentLayout>} />

                {/* 404 Route with header */}
                <Route path="*" element={<Layout><NotFound /></Layout>} />
              </Routes>
            </FilterProvider>
          </StudentAuthProvider>
          </SubAgnetAuthProvider>
        </AuthProvider>
      </Router>
    </Provider>
  );
};

export default App;